import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const alertVariants = cva(
  'relative flex w-full flex-col gap-1.5 rounded-2xl p-4 text-sm ring-1 backdrop-blur-sm',
  {
    variants: {
      tone: {
        danger:
          'bg-rose-500/12 text-rose-100 ring-rose-500/40 [&_strong]:text-rose-200',
        warning:
          'bg-amber-500/12 text-amber-100 ring-amber-500/40 [&_strong]:text-amber-200',
        info:
          'bg-sky-500/12 text-sky-100 ring-sky-500/40 [&_strong]:text-sky-200',
        success:
          'bg-emerald-500/12 text-emerald-100 ring-emerald-500/40 [&_strong]:text-emerald-200',
      },
    },
    defaultVariants: {
      tone: 'info',
    },
  },
);

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, tone, ...props }, ref) => (
    <div
      ref={ref}
      role="alert"
      className={cn(alertVariants({ tone }), className)}
      {...props}
    />
  ),
);
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('text-[13px] font-semibold tracking-tight', className)}
    {...props}
  />
));
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-[13px] leading-relaxed opacity-90', className)}
    {...props}
  />
));
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription };
